import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Heading,
  VStack,
  HStack,
  Avatar,
  Input,
  Divider,
  Box,
  FormLabel,
  Tooltip,
  Text,
  useToast,
  Flex,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useAtomValue } from "jotai";
import { BiEdit, BiSave, BiX, BiTrash } from "react-icons/bi";
import { MdAccountTree, MdAdd, MdDeleteForever, MdFileDownload } from "react-icons/md";
import { AccountAtom } from "../../store";
import { SecondaryButton, Dialog } from "../design_library";
import useAccountController from "../AccountSelector/useAccountController";
import React, { useMemo, useRef, useState } from "react";
import ModalHeader from "../design_library/ModalHeader";
import ExportAccount from "../ExportAccount/ExportAccount";
import { IoRadioButtonOff, IoRadioButtonOn } from "react-icons/io5";
import { getTradesAsImportCSV } from "../../api/Trades";

export const MangageAccounts = () => {
  const controller = useAccountController();
  const accountState = useAtomValue(AccountAtom);
  const toast = useToast();
  const [editingAccountId, setEditingAccountId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [confirmState, setConfirmState] = useState<
    | { type: "deleteTrades" | "deletePortfolio"; account: any }
    | null
  >(null);
  const cancelRef = useRef<any>(null);

  const handleStartEditing = (account: any) => {
    setEditingAccountId(account.id);
    setEditingName(account.name);
  };

  const handleSaveAccountName = (accountId: number) => {
    if (editingName.trim() === "") {
      toast({
        title: "Account name cannot be empty",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const account = accountState.accounts.find(acc => acc.id === accountId);
    if (account) {
      controller.updateAccountName(accountId, editingName, account);
      setEditingAccountId(null);
      
      toast({
        title: "Account name updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const filteredAccounts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return accountState.accounts.slice().sort((a: any, b: any) => a.id - b.id);
    return accountState.accounts
      .filter((a: any) => a.name?.toLowerCase().includes(query))
      .sort((a: any, b: any) => a.id - b.id);
  }, [accountState.accounts, searchQuery]);

  const handleQuickExport = async (accountId: number) => {
    const response = await getTradesAsImportCSV(accountId);
    const blob = response;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades_by_custodian-${new Date().toISOString()}.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <SecondaryButton
        variant="ghost"
        justifyContent="center"
        onClick={() => {
          controller.setModalOpen(true);
        }}
        width="full"
        leftIcon={<MdAccountTree />}
      >
        Manage portfolios
      </SecondaryButton>

      <Modal
        size={"xl"}
        isOpen={controller.modalOpen}
        onClose={() => controller.setModalOpen(false)}
      >
        <ModalOverlay backdropFilter="blur(3px)" />

        <ModalContent bg="var(--bg)" maxW={["92vw", "640px"]}>
          <ModalHeader
            icon={<MdAccountTree />}
            title={"Portfolio Management"}
            description={
              "Export your trades, update & delete your portfolios, or create a new one."
            }
          />
          <ModalBody pb="40px" pt="20px">
            <Heading fontSize={"md"} mb="8px">
              Your portfolios
            </Heading>
            <Text fontSize="sm" color="var(--text-muted)" mb="8px">
              {filteredAccounts.length} shown • {accountState.accounts.length} total
            </Text>

            <InputGroup mb="14px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.500" />
              </InputLeftElement>
              <Input
                placeholder="Search portfolios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Box maxH="320px" overflowY="auto" pr="2px" mb="24px">
              <VStack spacing={3} align="stretch">
                {filteredAccounts.map((account: any) => (
                  <Box
                    key={account.id}
                    p={3}
                    borderRadius="10px"
                    border="1px solid var(--gray)"
                    bg="var(--dark-gray)"
                    _hover={{ borderColor: "var(--primary)", bg: "rgba(255,255,255,0.02)" }}
                    transition="all 0.2s"
                    role="group"
                  >
                    <HStack
                      w="full"
                      justifyContent={"space-between"}
                    >
                      <HStack spacing={3}>
                        <Avatar
                          bg={"var(--gray)"}
                          name={account.name}
                          w="40px"
                          h="40px"
                          borderRadius={6}
                          color={"var(--white)"}
                        />
                        
                        {editingAccountId === account.id ? (
                          <HStack align="center" maxW={["60vw", "420px"]}>
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              placeholder="Enter portfolio name"
                              size="md"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveAccountName(account.id);
                                }
                                if (e.key === "Escape") {
                                  setEditingAccountId(null);
                                }
                              }}
                            />
                            <IconButton
                              aria-label="Save name"
                              icon={<BiSave />}
                              size="sm"
                              onClick={() => handleSaveAccountName(account.id)}
                              isDisabled={
                                editingName.trim() === "" || editingName.trim() === account.name?.trim()
                              }
                            />
                            <IconButton
                              aria-label="Cancel editing"
                              icon={<BiX />}
                              size="sm"
                              onClick={() => setEditingAccountId(null)}
                              variant="ghost"
                            />
                          </HStack>
                        ) : (
                          <HStack>
                            <Text fontWeight="medium">{account.name}</Text>
                            <Tooltip label="Edit name">
                              <IconButton
                                aria-label="Edit account name"
                                icon={<BiEdit />}
                                size="xs"
                                variant="ghost"
                                onClick={() => handleStartEditing(account)}
                                _groupHover={{ opacity: 1 }}
                                opacity={0.8}
                              />
                            </Tooltip>
                          </HStack>
                        )}
                      </HStack>

                      <HStack spacing={1}>
                        <Tooltip label={account.id === accountState.selectedAccount?.id ? "Selected" : "Set selected"}>
                          <IconButton
                            aria-label="Set selected"
                            icon={
                              account.id === accountState.selectedAccount?.id ? (
                                <IoRadioButtonOn />
                              ) : (
                                <IoRadioButtonOff />
                              )
                            }
                            size="sm"
                            variant="ghost"
                            onClick={() => controller.setSelectedAccount(account)}
                          />
                        </Tooltip>
                        <Tooltip label="Export trades">
                          <IconButton
                            aria-label="Export trades"
                            icon={<MdFileDownload />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleQuickExport(account.id)}
                          />
                        </Tooltip>
                        <Tooltip label="Delete all trades">
                          <IconButton
                            aria-label="Delete all trades"
                            icon={<BiTrash />}
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmState({ type: "deleteTrades", account })}
                          />
                        </Tooltip>
                        <Tooltip label="Delete portfolio">
                          <IconButton
                            aria-label="Delete portfolio"
                            icon={<MdDeleteForever />}
                            size="sm"
                            variant="ghost"
                            isDisabled={accountState.accounts.length === 1}
                            onClick={() => setConfirmState({ type: "deletePortfolio", account })}
                          />
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>
            <Divider mb="20px" />
            <Box mb="24px">
              <form onSubmit={(e) => {
                e.preventDefault();
                controller.handleSubmit(e);
                toast({
                  title: controller.editingId ? "Portfolio updated" : "Portfolio created",
                  status: "success",
                  duration: 2000,
                  isClosable: true,
                });
              }}>
                <FormLabel fontWeight="medium">Add new portfolio</FormLabel>
                <HStack align="center">
                  <Input
                    type="text"
                    name="name"
                    value={controller.accountForm.name}
                    onChange={controller.handleFormChange}
                    placeholder="Enter new portfolio name"
                    required
                  />
                  <SecondaryButton type="submit" leftIcon={<MdAdd />}>
                    {controller.editingId ? "Update" : "Create"}
                  </SecondaryButton>
                </HStack>
              </form>
            </Box>

            {/* <Divider mb="20px" />
            <Heading fontSize={"md"} mb="16px">
              Export Data
            </Heading>
            <ExportAccount /> */}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Confirm dialogs for icon-only destructive actions */}
      <AlertDialog
        isOpen={!!confirmState}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmState(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="var(--dark-gray)">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmState?.type === "deleteTrades"
                ? "Delete all transactions"
                : "Remove Portfolio"}
            </AlertDialogHeader>
            <AlertDialogBody>
              {confirmState?.type === "deleteTrades"
                ? `Are you sure you want to delete all transactions for portfolio "${confirmState?.account?.name}"?`
                : `Are you sure you want to delete "${confirmState?.account?.name}" and all associated transactions?`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmState(null)}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                onClick={() => {
                  if (!confirmState) return;
                  if (confirmState.type === "deleteTrades") {
                    controller.deleteAllTheTrades(confirmState.account.id);
                    toast({
                      title: "All trades deleted",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  } else {
                    controller.handleDelete(confirmState.account.id);
                    toast({
                      title: "Portfolio deleted",
                      status: "success",
                      duration: 2000,
                      isClosable: true,
                    });
                  }
                  setConfirmState(null);
                }}
              >
                Confirm
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
